import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { generateBoardReport } from '../generateReport'; 
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// --- ANIMATION VARIANTS ---
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 }
  }
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  show: { opacity: 1, scale: 1, transition: { type: "spring", duration: 0.3 } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2 } }
};

export default function Dashboard() {
  const { user, token, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // BILLING STATE
  const [billing, setBilling] = useState(null);

  // MODAL STATES
  const [showEmptyModal, setShowEmptyModal] = useState(false);
  const [reportModal, setReportModal] = useState({ show: false, title: '', message: '', type: 'success' });
  const [discoveryModal, setDiscoveryModal] = useState({ show: false, challengeText: '' });
  const [policyModal, setPolicyModal] = useState({ show: false, content: '', loading: false });
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info', action: null, actionText: '' });

  // ANALYTICS STATES
  const [inventory, setInventory] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [stats, setStats] = useState({
    inventoryCount: 0,
    shadowAiCount: 0,
    avgRiskScore: 0,
    euCompliance: 0,
    nistCompliance: 0,
    recentAudits: [],
    criticalPrivacy: 0,
    criticalSecurity: 0,
    criticalCompliance: 0
  });

  // SUPER ADMIN OVERRIDE
  const isSuperAdmin = user?.admin_role === 'SUPER_ADMIN' || user?.is_system_admin;
  const effectiveTier = isSuperAdmin ? 2 : (billing?.tier || 0);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const authToken = token || localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${authToken}` };
        
        const [invRes, progRes, auditRes, billingRes] = await Promise.all([
          axios.get(`${baseURL}/api/inventory`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${baseURL}/api/progress/summary`, { headers }).catch(() => ({ data: {} })),
          axios.get(`${baseURL}/api/audit`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${baseURL}/api/billing/status/${user.org_id}`, { headers }).catch(() => ({ data: null }))
        ]);

        const invData = Array.isArray(invRes.data) ? invRes.data : [];
        const auditData = Array.isArray(auditRes.data) ? auditRes.data : [];
        
        setInventory(invData);
        setAuditLog(auditData);
        setBilling(billingRes.data);

        const shadowAi = invData.filter(item => item.is_shadow === true || item.status === 'Shadow');
        const totalRisk = invData.reduce((acc, item) => acc + (item.risk_score || 0), 0);
        const avgRisk = invData.length > 0 ? Math.round(totalRisk / invData.length) : 0;
        const completionPct = progRes.data?.completion_pct || 0;

        let privCount = 0;
        let secCount = 0;
        let compCount = 0;

        invData.forEach(tool => {
          if (tool.risk_dimensions) {
            if (tool.risk_dimensions.data_privacy?.score >= 8) privCount++;
            if (tool.risk_dimensions.security_posture?.score >= 8) secCount++;
            if (tool.risk_dimensions.regulatory_compliance?.score >= 8) compCount++;
          }
        });

        setStats({
          inventoryCount: invData.length,
          shadowAiCount: shadowAi.length,
          avgRiskScore: avgRisk,
          euCompliance: completionPct,
          nistCompliance: Math.max(0, completionPct - 5),
          recentAudits: auditData.slice(0, 3),
          criticalPrivacy: privCount,
          criticalSecurity: secCount,
          criticalCompliance: compCount
        });
      } catch (error) {
        console.error("Dashboard fetch error:", error);
      } finally {
        setLoading(false);
      }
    };
    
    if (user) fetchDashboardData();
  }, [user, token]);

  const fetchBillingOnly = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await axios.get(`${baseURL}/api/billing/status/${user.org_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBilling(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpgrade = async (targetTier, challengeText = '') => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      await axios.post(`${baseURL}/api/billing/request-upgrade`, { 
        orgId: user.org_id, 
        targetTier,
        challenge: challengeText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAlertModal({ 
        show: true, 
        title: 'Request Sent', 
        message: targetTier === 2 && challengeText 
          ? 'Upgrade request and discovery notes sent successfully! An admin will review it shortly.' 
          : 'Upgrade request sent successfully! An admin will review it shortly.', 
        type: 'success' 
      });
      setDiscoveryModal({ show: false, challengeText: '' });
      fetchBillingOnly();
    } catch (err) {
      setAlertModal({ 
        show: true, 
        title: 'Error', 
        message: 'Failed to send upgrade request. Please try again.', 
        type: 'error' 
      });
    }
  };

  const handleViewPolicy = async () => {
    const hasSanctionedTools = inventory.some(t => t.sanctioned === true || t.status === 'Approved');
    if (!hasSanctionedTools) {
        setAlertModal({ 
            show: true, 
            title: 'Action Required', 
            message: 'You need at least one Approved/Sanctioned AI tool in your inventory to generate a corporate policy. Please go to your Inventory and approve a tool first.', 
            type: 'error' 
        });
        return;
    }

    setPolicyModal({ show: true, content: '', loading: true });
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const headers = { Authorization: `Bearer ${token}` };
      const { data } = await axios.get(`${baseURL}/api/inventory/policy`, { headers });
      
      if (data.policy) {
        setPolicyModal({ show: true, content: data.policy, loading: false });
      } else {
        handleGeneratePolicy(); 
      }
    } catch (err) {
      console.error(err);
      setPolicyModal({ show: false, content: '', loading: false });
      setAlertModal({ show: true, title: 'Error', message: 'Failed to fetch policy.', type: 'error' });
    }
  };

  const handleGeneratePolicy = async () => {
    setPolicyModal({ show: true, content: '', loading: true });
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const headers = { Authorization: `Bearer ${token}` };
      const { data } = await axios.post(`${baseURL}/api/inventory/generate-policy`, {}, { headers });
      setPolicyModal({ show: true, content: data.policy, loading: false });
    } catch (err) {
      setPolicyModal({ show: false, content: '', loading: false });
      setAlertModal({ show: true, title: 'Error', message: err.response?.data?.error || 'Failed to generate policy.', type: 'error' });
    }
  };

  const handleDownloadPolicy = () => {
    const element = document.createElement("a");
    const file = new Blob([policyModal.content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${user?.org_name?.replace(/\s+/g, '_') || 'Corporate'}_AI_Policy.md`;
    document.body.appendChild(element); 
    element.click();
    document.body.removeChild(element);
  };

  const handleReviewRequest = () => {
    if (!isSuperAdmin && (!billing || billing.tier < 2)) {
        setAlertModal({ 
          show: true, 
          title: 'Tier 2 Feature', 
          message: 'Expert Report Review requires Tier 2 Sprint Access. Would you like to submit a request to upgrade your account?', 
          type: 'info',
          action: () => handleUpgrade(2),
          actionText: 'Request Upgrade'
        });
        return;
    }

    if (!isSuperAdmin && billing.report_reviews_available <= 0) {
        setAlertModal({ 
          show: true, 
          title: 'Limit Reached', 
          message: 'You have already utilized your allocated Expert Review.', 
          type: 'error' 
        });
        return;
    }

    setAlertModal({ 
      show: true, 
      title: 'Review Initiated', 
      message: 'Expert Review Initiated! Our team will contact you shortly.', 
      type: 'success' 
    });
  };

  const handleGenerateReport = async () => {
    if (!isSuperAdmin && billing && billing.used_board_reports >= billing.max_board_reports) {
      setReportModal({
        show: true,
        title: 'Limit Reached',
        message: 'You have reached your maximum Board Report generation limit for your current tier. Please upgrade your account to generate more.',
        type: 'error'
      });
      return;
    }

    if (!isSuperAdmin && billing && billing.tier === 0 && billing.trial_end_date && new Date(billing.trial_end_date) < new Date()) { 
      setReportModal({
        show: true,
        title: 'Trial Expired',
        message: 'Your free trial has expired. Report generation is locked. Upgrade to Tier 1 to restore access.',
        type: 'error'
      });
      return;
    }

    if (inventory.length === 0) {
      setShowEmptyModal(true);
      return;
    }

    try {
      if (typeof generateBoardReport === 'function') {
        await generateBoardReport(user, inventory, auditLog);
        
        if (billing) {
           setBilling({...billing, used_board_reports: billing.used_board_reports + 1});
        }

        setReportModal({
          show: true,
          title: 'Report Generated',
          message: 'Your AI Governance Board Report has been successfully generated and downloaded.',
          type: 'success'
        });
      } else {
        setReportModal({
          show: true,
          title: 'System Initializing',
          message: 'The report generator is currently initializing. Please try again in a moment.',
          type: 'error'
        });
      }
    } catch (err) {
      console.error(err);
      setReportModal({
        show: true,
        title: 'Generation Failed',
        message: 'There was an error generating your report. Please check your connection and try again.',
        type: 'error'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-slate-200 border-t-blue-600 rounded-full"
        />
      </div>
    );
  }

  const isFreeTrial = !isSuperAdmin && billing?.tier === 0;
  const daysLeft = billing?.trial_end_date 
    ? Math.ceil((new Date(billing.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24))
    : 0;
  const isExpired = isFreeTrial && daysLeft < 0;

  const liveAiToolsCount = auditLog.filter(a => a.action === 'TOOL_ADDED').length || billing?.used_ai_tools || 0;
  const liveRiskAnalysesCount = auditLog.filter(a => a.action === 'CROSS_TOOL_ANALYSIS_COMPLETED').length || billing?.used_risk_analyses || 0;

  const renderProgressBar = (label, used, max) => {
    if (isSuperAdmin) {
      return (
        <motion.div variants={itemVariants} className="bg-white p-5 rounded-xl border border-purple-200 shadow-sm relative overflow-hidden group hover:border-purple-300 transition-colors">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-purple-500"></div>
          <div className="flex justify-between items-center mb-2">
            <span className="font-semibold text-slate-700">{label}</span>
            <span className="text-sm font-bold text-purple-600">Unlimited ∞</span>
          </div>
          <div className="w-full bg-purple-100 rounded-full h-2.5 overflow-hidden">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 1, delay: 0.5 }}
              className="bg-purple-500 h-2.5 rounded-full" 
            />
          </div>
        </motion.div>
      );
    }

    const percent = Math.min((used / max) * 100, 100);
    let color = 'bg-blue-500';
    if (percent >= 100) color = 'bg-red-500';
    else if (percent >= 80) color = 'bg-amber-500';

    return (
      <motion.div variants={itemVariants} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm group hover:border-slate-300 transition-colors">
        <div className="flex justify-between items-center mb-2">
          <span className="font-semibold text-slate-700">{label}</span>
          <span className="text-sm font-medium text-slate-500">{used} / {max}</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${percent}%` }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
            className={`${color} h-2.5 rounded-full`} 
          />
        </div>
        {used >= max && <p className="text-xs text-red-500 mt-2 font-medium">Limit Reached</p>}
      </motion.div>
    );
  };

  return (
    <motion.div 
      className="p-8 max-w-7xl mx-auto space-y-8 pb-24 relative"
      variants={containerVariants}
      initial="hidden"
      animate="show"
    >
      
      {/* MODALS WITH ANIMATEPRESENCE */}
      <AnimatePresence>
        {alertModal.show && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <motion.div variants={modalVariants} initial="hidden" animate="show" exit="exit" className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl mx-auto mb-4 ${
                alertModal.type === 'success' ? 'bg-emerald-100 text-emerald-600' : 
                alertModal.type === 'info' ? 'bg-blue-100 text-blue-600' : 'bg-red-100 text-red-600'
              }`}>
                {alertModal.type === 'success' ? '✓' : alertModal.type === 'info' ? 'ℹ' : '⚠'}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{alertModal.title}</h3>
              <p className="text-slate-500 mb-6 text-sm leading-relaxed">{alertModal.message}</p>
              
              {alertModal.action ? (
                 <div className="flex gap-3">
                   <button onClick={() => setAlertModal({ show: false, title: '', message: '', type: 'info', action: null, actionText: '' })} className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 font-bold rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
                   <button onClick={() => { alertModal.action(); setAlertModal({ show: false, title: '', message: '', type: 'info', action: null, actionText: '' }); }} className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors">{alertModal.actionText}</button>
                 </div>
              ) : (
                 <button onClick={() => setAlertModal({ show: false, title: '', message: '', type: 'info', action: null, actionText: '' })} className="w-full px-4 py-2.5 bg-slate-900 text-white font-bold rounded-lg hover:bg-slate-800 transition-colors">Acknowledge</button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {policyModal.show && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div variants={modalVariants} initial="hidden" animate="show" exit="exit" className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50 rounded-t-2xl">
                <h3 className="text-xl font-bold text-slate-900">Corporate AI Policy</h3>
                <button onClick={() => setPolicyModal({ show: false, content: '', loading: false })} className="text-slate-400 hover:text-slate-600 text-2xl">&times;</button>
              </div>
              <div className="p-8 overflow-y-auto text-sm text-slate-700 font-serif leading-relaxed">
                {policyModal.loading ? (
                  <div className="flex flex-col items-center justify-center py-10">
                    <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full mb-4" />
                    <p className="text-slate-500 font-bold">Drafting legal policy based on your inventory...</p>
                  </div>
                ) : (
                  <div className="prose prose-slate max-w-none prose-tables:w-full prose-tables:border prose-th:bg-slate-50 prose-th:p-3 prose-td:p-3 prose-td:border-t">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{policyModal.content}</ReactMarkdown>
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl flex justify-between gap-3">
                <motion.button whileHover={{ scale: 1.02 }} onClick={handleDownloadPolicy} disabled={policyModal.loading} className="px-4 py-2 border border-slate-300 text-slate-700 font-bold rounded-lg hover:bg-white flex items-center gap-2 disabled:opacity-50">
                  ↓ Download (.txt)
                </motion.button>
                <div className="flex gap-3">
                  <motion.button whileHover={{ scale: 1.02 }} onClick={handleGeneratePolicy} disabled={policyModal.loading} className="px-4 py-2 border border-slate-300 text-slate-700 font-bold rounded-lg hover:bg-white disabled:opacity-50">Regenerate</motion.button>
                  <motion.button whileHover={{ scale: 1.02 }} onClick={() => setPolicyModal({ show: false, content: '', loading: false })} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700">Accept & Close</motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {discoveryModal.show && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[75] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div variants={modalVariants} initial="hidden" animate="show" exit="exit" className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 transform transition-all">
              <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-4">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-2xl">🚀</div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Book Discovery Call</h3>
                  <p className="text-sm text-slate-500">Initiate your Tier 2 Readiness Sprint</p>
                </div>
              </div>
              
              <label className="block text-sm font-bold text-slate-800 mb-2">
                What is your biggest AI challenge or question right now? <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-slate-500 mb-3">In 2-3 sentences, describe where you are stuck or what you most want clarity on.</p>
              
              <textarea 
                className="w-full border border-slate-300 rounded-lg p-4 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-shadow min-h-[120px]"
                placeholder="e.g., We want to deploy an internal LLM, but our legal team is blocking it due to data privacy concerns..."
                value={discoveryModal.challengeText}
                onChange={(e) => setDiscoveryModal({ ...discoveryModal, challengeText: e.target.value })}
              />
              <div className="flex gap-3 mt-8">
                <button onClick={() => setDiscoveryModal({ show: false, challengeText: '' })} className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 font-bold rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
                <button onClick={() => handleUpgrade(2, discoveryModal.challengeText)} disabled={!discoveryModal.challengeText.trim()} className="flex-1 px-4 py-3 bg-purple-600 text-white font-bold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md">Submit & Request Sprint</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {reportModal.show && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <motion.div variants={modalVariants} initial="hidden" animate="show" exit="exit" className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl mx-auto mb-4 ${
                reportModal.type === 'success' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
              }`}>
                {reportModal.type === 'success' ? '✓' : '⚠'}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{reportModal.title}</h3>
              <p className="text-slate-500 mb-6 text-sm leading-relaxed">{reportModal.message}</p>
              <button onClick={() => setReportModal({ show: false, title: '', message: '', type: 'success' })} className="w-full px-4 py-2.5 bg-slate-900 text-white font-bold rounded-lg hover:bg-slate-800 transition-colors">Acknowledge</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEmptyModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <motion.div variants={modalVariants} initial="hidden" animate="show" exit="exit" className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 transform transition-all">
              <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-2xl mb-4">⚠</div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Inventory is Empty</h3>
              <p className="text-slate-500 mb-6 text-sm leading-relaxed">You must add at least one AI model or tool to your inventory before the system can generate a governance and risk report.</p>
              <div className="flex gap-3">
                <button onClick={() => setShowEmptyModal(false)} className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors">Cancel</button>
                <button onClick={() => navigate('/inventory')} className="flex-1 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors">Go to Inventory</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* HEADER SECTION */}
      <motion.div variants={itemVariants} className="flex justify-between items-end border-b border-slate-200 pb-6">
        <div>
          <h2 className="text-3xl font-bold text-slate-900 tracking-tight">Welcome back, {user?.full_name?.split(' ')[0] || 'Leader'}</h2>
          <p className="text-slate-500 mt-2">Monitor compliance limits and platform usage for {user?.org_name || 'your organization'}.</p>
        </div>
        
        <motion.button 
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleGenerateReport}
          disabled={isImpersonating}
          className={`font-bold px-6 py-2.5 rounded-lg shadow transition-colors flex items-center gap-2 ${
            isImpersonating ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <span>📄</span> {isImpersonating ? 'Read-Only' : 'Generate Report'}
        </motion.button>
      </motion.div>

      {/* BILLING & TIER BANNERS */}
      <motion.div variants={itemVariants}>
        {isSuperAdmin ? (
          <div className="bg-purple-50 border border-purple-200 text-purple-800 p-4 rounded-xl flex items-center justify-between shadow-sm">
            <div>
              <h3 className="font-bold flex items-center gap-2">⚡ Super Admin Mode Active</h3>
              <p className="text-sm mt-1">You have unlimited access to all Tier 1 and Tier 2 features. Platform billing limits are bypassed.</p>
            </div>
            <Link to="/admin" className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors whitespace-nowrap shadow-sm">
              Open Admin Panel
            </Link>
          </div>
        ) : billing && (
          <>
            {isFreeTrial && !isExpired && (
              <div className="bg-blue-50 border border-blue-200 text-blue-800 p-4 rounded-xl flex items-center justify-between shadow-sm">
                <div>
                  <h3 className="font-bold flex items-center gap-2">⏱ Free Trial Active</h3>
                  <p className="text-sm mt-1">Your trial expires in <span className="font-bold">{daysLeft} days</span>. You are limited to 3 AI tools, 3 Risk Analyses, and 1 Board Report.</p>
                </div>
                <button 
                  onClick={() => handleUpgrade(1)}
                  disabled={billing.upgrade_status === 'PENDING_TIER_1' || isImpersonating}
                  className={`font-semibold py-2 px-4 rounded-lg transition-colors whitespace-nowrap ${
                    isImpersonating ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50'
                  }`}
                >
                  {isImpersonating ? 'Read-Only' : (billing.upgrade_status === 'PENDING_TIER_1' ? 'Upgrade Pending...' : 'Upgrade to Tier 1')}
                </button>
              </div>
            )}
            {isExpired && (
              <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-xl flex items-center justify-between shadow-sm">
                <div>
                  <h3 className="font-bold flex items-center gap-2">⚠ Free Trial Expired</h3>
                  <p className="text-sm mt-1">Your free trial has expired. Data input and report generation are locked. Upgrade to Tier 1 to restore access.</p>
                </div>
                <button 
                  onClick={() => handleUpgrade(1)}
                  disabled={billing.upgrade_status === 'PENDING_TIER_1' || isImpersonating}
                  className={`font-semibold py-2 px-4 rounded-lg transition-colors whitespace-nowrap ${
                    isImpersonating ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 text-white disabled:opacity-50'
                  }`}
                >
                  {isImpersonating ? 'Read-Only' : (billing.upgrade_status === 'PENDING_TIER_1' ? 'Upgrade Pending...' : 'Upgrade to Tier 1')}
                </button>
              </div>
            )}
            {billing.tier === 1 && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-4 rounded-xl flex items-center justify-between shadow-sm">
                <div>
                  <h3 className="font-bold flex items-center gap-2">✓ Tier 1 Active</h3>
                  <p className="text-sm mt-1">You have unlocked standard limits (20 tools, 20 reports). Need expert guidance? Upgrade to the Tier 2 Sprint.</p>
                </div>
                <button 
                  onClick={() => setDiscoveryModal({ show: true, challengeText: '' })}
                  disabled={billing.upgrade_status === 'PENDING_TIER_2' || isImpersonating}
                  className={`font-semibold py-2 px-4 rounded-lg transition-colors whitespace-nowrap ${
                    isImpersonating ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50'
                  }`}
                >
                  {isImpersonating ? 'Read-Only' : (billing.upgrade_status === 'PENDING_TIER_2' ? 'Sprint Request Pending...' : 'Request Tier 2 Sprint')}
                </button>
              </div>
            )}
          </>
        )}
      </motion.div>

      {/* USAGE LIMITS GRID */}
      {(billing || isSuperAdmin) && (
        <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {renderProgressBar("AI Tools Monitored", liveAiToolsCount, billing?.max_ai_tools || 0)}
          {renderProgressBar("Risk Analyses", liveRiskAnalysesCount, billing?.max_risk_analyses || 0)}
          {renderProgressBar("Board Reports", billing?.used_board_reports || 0, billing?.max_board_reports || 0)}
        </motion.div>
      )}

      {/* Corporate AI Policy Card */}
      <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between mt-6 group hover:border-slate-300 transition-colors">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xl">📄</div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">Corporate AI Use Policy</h3>
            <p className="text-sm font-medium text-slate-500">Auto-generated governance document based on your approved inventory.</p>
          </div>
        </div>
        <motion.button 
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleViewPolicy}
          disabled={isImpersonating}
          className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-lg transition-colors"
        >
          {isImpersonating ? 'Read-Only' : 'View / Generate Policy'}
        </motion.button>
      </motion.div>

      {/* TOP ANALYTICS ROW */}
      <motion.h2 variants={itemVariants} className="text-xl font-bold text-slate-800 mt-8">Compliance Overview</motion.h2>
      <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4 hover:-translate-y-1 transition-transform">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 text-xl">📊</div>
          <div>
            <p className="text-sm font-medium text-slate-500">Average AI Risk Score</p>
            <div className="flex items-baseline gap-2">
              <h3 className="text-2xl font-bold text-slate-900">{stats.avgRiskScore}/10</h3>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${stats.avgRiskScore > 6 ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-500'}`}>
                {stats.avgRiskScore > 6 ? 'High Risk' : 'Safe'}
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4 hover:-translate-y-1 transition-transform">
          <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xl">🤖</div>
          <div>
            <p className="text-sm font-medium text-slate-500">Active AI Models</p>
            <div className="flex items-baseline gap-2">
              <h3 className="text-2xl font-bold text-slate-900">{stats.inventoryCount}</h3>
              <span className="text-xs font-medium text-slate-400">Models Tracked</span>
            </div>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4 hover:-translate-y-1 transition-transform">
          <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 text-xl">🕵️</div>
          <div>
            <p className="text-sm font-medium text-slate-500">Shadow AI Detection</p>
            <div className="flex items-baseline gap-2">
              <h3 className="text-2xl font-bold text-rose-600">{stats.shadowAiCount}</h3>
              <span className="text-xs font-medium text-rose-500">Require Review</span>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* SPLIT ROW: Deep Risk & Audit Trails */}
      <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2"><span>🔍</span> Deep Risk Analysis</h3>
            <Link to="/risk-analysis" className="text-sm font-medium text-blue-600 hover:text-blue-700">View Full Analysis</Link>
          </div>
          <div className="space-y-4 flex-1">
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border border-slate-100 transition-colors hover:bg-white">
              <span className="font-medium text-slate-700">Regulatory & Compliance Gaps</span>
              {stats.criticalCompliance > 0 
                ? <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded shadow-sm">{stats.criticalCompliance} Critical</span>
                : <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded shadow-sm">0 Critical</span>}
            </div>
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border border-slate-100 transition-colors hover:bg-white">
              <span className="font-medium text-slate-700">Data Privacy Vulnerabilities</span>
              {stats.criticalPrivacy > 0 
                ? <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded shadow-sm">{stats.criticalPrivacy} Critical</span>
                : <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded shadow-sm">0 Critical</span>}
            </div>
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border border-slate-100 transition-colors hover:bg-white">
              <span className="font-medium text-slate-700">Security / LLM Posture</span>
              {stats.criticalSecurity > 0 
                ? <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded shadow-sm">{stats.criticalSecurity} Critical</span>
                : <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded shadow-sm">0 Critical</span>}
            </div>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
            <h3 className="font-bold text-slate-800 flex items-center gap-2"><span>📜</span> Immutable Audit Trails</h3>
            <span className="text-xs text-slate-500">Last 30 Days</span>
          </div>
          <div className="p-6 text-slate-500 flex-1 flex flex-col justify-center">
            {stats.recentAudits.length > 0 ? (
              <ul className="text-left space-y-3">
                {stats.recentAudits.map((audit, index) => (
                  <motion.li 
                    initial={{ opacity: 0, x: -10 }} 
                    animate={{ opacity: 1, x: 0 }} 
                    transition={{ delay: index * 0.1 }}
                    key={index} 
                    className="text-sm border-b pb-2 flex items-center gap-2"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                    {audit.action} - {new Date(audit.created_at).toLocaleDateString()}
                  </motion.li>
                ))}
              </ul>
            ) : (
              <p className="text-center py-4">No recent compliance events or system changes logged. Your audit trail is clean.</p>
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* EXPERT REVIEW SECTION */}
      {effectiveTier >= 1 && (
        <motion.div variants={itemVariants} className="bg-slate-900 rounded-xl p-8 text-white shadow-xl flex items-center justify-between border border-slate-800">
           <div className="max-w-xl">
              <h3 className="text-2xl font-bold mb-2 flex items-center gap-3">🧠 Expert Report Review</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Submit your generated Board Report directly to a Fractional Chief AI Officer for a comprehensive manual review. Ensure your compliance narrative is bulletproof before presenting to stakeholders.
              </p>
              {effectiveTier >= 2 && (
                  <div className="mt-4 inline-block bg-purple-500/20 border border-purple-500/30 text-purple-300 text-xs font-bold px-3 py-1 rounded-full">
                      Reviews Available: {isSuperAdmin ? 'Unlimited ∞' : billing?.report_reviews_available}
                  </div>
              )}
           </div>
           
           <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleReviewRequest}
              disabled={isImpersonating}
              className={`font-bold py-3 px-6 rounded-lg transition-colors shadow-lg ${
                isImpersonating 
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed' 
                  : effectiveTier >= 2 
                    ? 'bg-purple-600 hover:bg-purple-500 text-white' 
                    : 'bg-slate-700 hover:bg-slate-600 text-slate-300 border border-slate-600'
              }`}
           >
              {isImpersonating ? 'Read-Only' : (effectiveTier >= 2 ? 'Submit for Review' : 'Unlock Feature')}
           </motion.button>
        </motion.div>
      )}

    </motion.div>
  );
}